import { GeoJSONSource, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { DrawingObjectGeometry, DrawingObjectType } from '../types';
import { CircleEditMode } from './circle-edit-mode';
import { FeCircleMode } from './circle-mode';
import { FeDrawMode } from './draw-mode';
import { FePolygonEditMode } from './polygon-edit-mode';
import { FePolygonMode } from './polygon-mode';
import { FeRectangleEditMode } from './rectangle-edit-mode';
import { FeRectangleMode } from './rectangle-mode';
import { setCursor } from '../../helpers/helpers';

export type FeDrawProperties = FeDrawCircleProperties | FeDrawPolygonProperties | FeDrawRectangleProperties;

export interface FeDrawCircleProperties {
    type: DrawingObjectType.circle;
    radius: number;
}

export interface FeDrawPolygonProperties {
    type: DrawingObjectType.polygon;
}

export interface FeDrawRectangleProperties {
    type: DrawingObjectType.rectangle;
    width: number;
    height: number;
}

export class FeDraw {
    private map: MaplibreMap | null = null;

    start(map: MaplibreMap) {
        this.map = map;
        if (!this.map.getSource('fe-drawing')) {
            this.map.addSource('fe-drawing', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });

            this.map.addLayer({
                id: 'fe-drawing-fill',
                filter: ['==', '$type', 'Polygon'],
                source: 'fe-drawing',
                type: 'fill',
                paint: {
                    'fill-color': '#0078fa',
                    'fill-opacity': 0.5
                }
            });

            this.map.addLayer({
                id: 'fe-drawing-line',
                filter: ['==', '$type', 'Polygon'],
                source: 'fe-drawing',
                type: 'line',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round'
                },
                paint: {
                    'line-color': '#0078fa',
                    'line-dasharray': [0.2, 2],
                    'line-width': 2
                }
            });

            this.map.addLayer({
                id: 'fe-drawing-points-outline',
                filter: ['all', ['==', '$type', 'Point']],
                source: 'fe-drawing',
                type: 'circle',
                paint: {
                    'circle-radius': [
                        'case',
                        ['==', ['get', 'type'], 'midpoint'],
                        0,
                        ['==', ['boolean', ['get', 'active'], false], true],
                        7,
                        5
                    ],
                    'circle-color': '#fff',
                    'circle-opacity': 1.0
                }
            });

            this.map.addLayer({
                id: 'fe-drawing-points-inner',
                filter: ['all', ['==', '$type', 'Point']],
                source: 'fe-drawing',
                type: 'circle',
                paint: {
                    'circle-radius': [
                        'case',
                        ['==', ['get', 'type'], 'midpoint'],
                        0,
                        ['==', ['boolean', ['get', 'active'], false], true],
                        5,
                        3
                    ],
                    'circle-color': '#FFAB00',
                    'circle-opacity': 1.0
                }
            });

            this.map.addLayer({
                id: 'fe-drawing-midpoints',
                filter: ['==', ['get', 'type'], 'midpoint'],
                source: 'fe-drawing',
                type: 'circle',
                paint: {
                    'circle-color': '#FFAB00',
                    'circle-opacity': 1.0,
                    'circle-radius': 3,
                    'circle-stroke-width': 10,
                    'circle-stroke-opacity': 0
                }
            });

            this.map.on('click', this.onClick);
            this.map.on('mousemove', this.onMove);
            this.map.on('mousedown', this.onMouseDown);
            this.map.on('mouseup', this.onMouseUp);
            document.addEventListener('keydown', this.onKeyDown);
        }
        const source = this.map.getSource('fe-drawing') as GeoJSONSource;

        this.modes = {
            circle: new FeCircleMode(this.map, source),
            rectangle: new FeRectangleMode(this.map, source),
            polygon: new FePolygonMode(this.map, source),
            polygonEdit: new FePolygonEditMode(this.map, source),
            rectangleEdit: new FeRectangleEditMode(this.map, source),
            circleEdit: new CircleEditMode(this.map, source)
        };
    }

    finalize() {
        this.modes = {} as any;
        this.map?.off('click', this.onClick);
        this.map?.off('mousemove', this.onMove);
        this.map?.off('mousedown', this.onMouseDown);
        this.map?.off('mouseup', this.onMouseUp);
        document.removeEventListener('keydown', this.onKeyDown);
        this.map = null;
        this.mode = null;
    }

    private modes!: {
        circle: FeCircleMode;
        rectangle: FeRectangleMode;
        polygon: FePolygonMode;
        polygonEdit: FePolygonEditMode;
        rectangleEdit: FeRectangleEditMode;
        circleEdit: CircleEditMode;
    };

    private mode: FeDrawMode | null = null;

    setMode<T extends keyof typeof this.modes>(mode: T | null) {
        this.mode?.cancel();
        setCursor(this.map!, mode ? 'crosshair' : null);

        if (mode === null) {
            this.mode = null;
            return;
        }

        this.mode = this.modes[mode];
        this.mode.draw();
    }

    private onClick = (e: MapMouseEvent) => {
        this.mode?.onClick(e);
    };

    private onMove = (e: MapMouseEvent) => {
        this.mode?.onMouseMove(e);
    };

    private onMouseDown = (e: MapMouseEvent) => {
        this.mode?.onMouseDown(e);
    };

    private onMouseUp = (e: MapMouseEvent) => {
        this.mode?.onMouseUp(e);
    };

    private onKeyDown = (e: KeyboardEvent) => {
        this.mode?.onKeyDown(e);

        if (e.key === 'Escape') {
            this.mode?.cancel();
        }
    };

    edit(type: DrawingObjectType, object: DrawingObjectGeometry) {
        this.mode?.cancel();
        if (type === DrawingObjectType.circle) {
            this.mode = this.modes['circleEdit'];
        } else if (type === DrawingObjectType.rectangle) {
            this.mode = this.modes['rectangleEdit'];
        } else {
            this.mode = this.modes['polygonEdit'];
        }

        this.mode.edit(object);
    }

    getEdited(): DrawingObjectGeometry {
        return this.mode!.getEdited();
    }
}
